import { MenuItemType, MenuType } from "@/types/baseTypes";
import React, { useRef } from "react";
import { MenuParentType } from "./Menu";

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

const isElementHidden = (element: HTMLElement) =>
  !!element.closest("[aria-hidden='true'], [hidden], [inert]");

const getFocusableElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (element) =>
      !isElementHidden(element) &&
      element.tabIndex !== -1 &&
      !element.hasAttribute("disabled"),
  );

type KeyboardNavigationProps = {
  setCurrentSubmenu: (value: MenuParentType) => void;
  subMenu: MenuParentType;
  currentSubmenu?: MenuParentType;
} & (
  | {
      menu: MenuType;
      type: "menu";
      controlParentMenu?: undefined;
    }
  | {
      menu: MenuItemType;
      type: "submenu";
      controlParentMenu: (action: "close" | "prev" | "next") => void;
    }
);

export const useKeyboardNavigation = ({
  menu,
  type,
  setCurrentSubmenu,
  controlParentMenu,
  subMenu,
  currentSubmenu,
}: KeyboardNavigationProps) => {
  const menuRef = useRef<HTMLUListElement>(null);

  const items = useRef<Array<React.RefObject<HTMLLIElement>>>([]);
  const focusedItem = useRef<number>(0);

  const currentMenuItem = () =>
    type === "submenu"
      ? menu.child_items?.[focusedItem.current]
      : type === "menu"
        ? menu.items?.[focusedItem.current]
        : undefined;

  const focusNext = () => {
    if (focusedItem.current === items.current.length - 1) {
      focusedItem.current = 0;
    } else {
      focusedItem.current++;
    }
    focusCurrentItem();
  };
  const focusPrevious = () => {
    if (focusedItem.current === 0) {
      focusedItem.current = items.current.length - 1;
    } else {
      focusedItem.current--;
    }
    focusCurrentItem();
  };
  const focusCurrentItem = () => {
    if (currentMenuItem()?.child_items) {
      items.current[focusedItem.current].current
        ?.querySelector("button")
        ?.focus();
    } else {
      items.current[focusedItem.current].current?.querySelector("a")?.focus();
    }
    setCurrentSubmenu(currentMenuItem());
  };

  const focusMenuItemAt = (index: number) => {
    const item = type === "menu" ? menu.items?.[index] : undefined;
    const itemElement = items.current[index]?.current;

    if (!item || !itemElement) return false;

    focusedItem.current = index;

    if (item.child_items) {
      itemElement.querySelector("button")?.focus();
    } else {
      itemElement.querySelector("a")?.focus();
    }

    setCurrentSubmenu(item);
    return true;
  };

  const getActiveMenuItemIndex = () => {
    const activeElement = document.activeElement;

    if (!activeElement) return -1;

    return items.current.findIndex((item) =>
      item.current?.contains(activeElement),
    );
  };

  const getOpenPanel = (index: number, activeElement: Element | null) => {
    const itemElement = items.current[index]?.current;

    if (!itemElement) return undefined;

    const activePanel = activeElement?.closest<HTMLElement>(
      ".mega-menu.is-open, .navbar-submenu.is-open",
    );

    if (activePanel && itemElement.contains(activePanel)) {
      return activePanel;
    }

    return itemElement?.querySelector<HTMLElement>(
      ".mega-menu.is-open, .navbar-submenu.is-open",
    );
  };

  const focusLastPanelElementAt = (index: number) => {
    const item = type === "menu" ? menu.items?.[index] : undefined;

    if (!item?.child_items) {
      focusMenuItemAt(index);
      return;
    }

    focusedItem.current = index;
    setCurrentSubmenu(item);

    setTimeout(() => {
      const openPanel = getOpenPanel(index, null);
      const focusableElements = openPanel
        ? getFocusableElements(openPanel)
        : [];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      if (lastFocusableElement) {
        lastFocusableElement.focus();
      } else {
        focusMenuItemAt(index);
      }
    }, 50);
  };

  const handleMenuTab = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (type !== "menu") return;

    const activeIndex = getActiveMenuItemIndex();
    if (activeIndex === -1) return;

    const activeElement = document.activeElement;
    const itemElement = items.current[activeIndex]?.current;
    const openPanel = getOpenPanel(activeIndex, activeElement);

    if (
      event.shiftKey &&
      itemElement?.querySelector("button, a") === activeElement
    ) {
      if (activeIndex === 0) return;

      event.preventDefault();
      event.stopPropagation();
      focusLastPanelElementAt(activeIndex - 1);
      return;
    }

    if (!openPanel) return;

    const focusableElements = getFocusableElements(openPanel);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (activeElement === firstFocusableElement) {
        event.preventDefault();
        event.stopPropagation();
        focusMenuItemAt(activeIndex);
      }

      return;
    }

    if (activeElement === lastFocusableElement) {
      if (activeIndex === items.current.length - 1) return;

      event.preventDefault();
      event.stopPropagation();
      focusMenuItemAt(activeIndex + 1);
    }
  };

  const keyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === "Tab") {
      handleMenuTab(event);
      return;
    }

    if (
      event.key === "ArrowRight" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Escape"
    ) {
      event.preventDefault();
      event.stopPropagation();

      //do not include "Enter" to keep links working
    }
    switch (event.key) {
      case "ArrowRight":
        if (type === "menu") {
          focusNext();
        }
        if (type === "submenu") {
          if (currentMenuItem()?.child_items) {
            setCurrentSubmenu(currentMenuItem());

            setTimeout(() => {
              (
                items.current[focusedItem.current].current?.querySelector(
                  "ul > li > a",
                ) as HTMLElement
              )?.focus();
            }, 50);
          } else {
            controlParentMenu("next");
          }
        }

        break;
      case "ArrowLeft":
        if (type === "menu") {
          focusPrevious();
        }
        if (type === "submenu") {
          controlParentMenu("prev");
        }

        break;
      case "ArrowDown":
        if (type === "submenu") {
          focusNext();
        }
      case "Enter":
        if (type === "menu") {
          const focusedMenuItem = menu.items?.[focusedItem.current];

          // Repeat Enter on an already-open item toggles its submenu closed,
          // mirroring the mouse-click toggle behavior.
          if (
            focusedMenuItem?.child_items &&
            currentSubmenu?.ID === focusedMenuItem.ID
          ) {
            setCurrentSubmenu(undefined);
            break;
          }

          setCurrentSubmenu(focusedMenuItem);

          setTimeout(() => {
            (
              items.current[focusedItem.current].current?.querySelector(
                "[data-mega-menu-first-link], :scope ul > li > a, :scope ul > li > button",
              ) as HTMLElement
            )?.focus();
          }, 50);
        }

        break;
      case "Escape":
        if (type === "submenu") {
          controlParentMenu("close");
        }
        break;
      case "ArrowUp":
        if (type === "submenu") {
          focusPrevious();
        }
    }
  };

  const controlMenu = (action: "close" | "prev" | "next") => {
    if (action === "prev") {
      focusPrevious();
    }
    if (action === "next") {
      focusNext();
    }
    if (action === "close") {
      setCurrentSubmenu(subMenu);
      items.current[focusedItem.current].current
        ?.querySelector("button")
        ?.focus();
    }
  };

  const firstItemFocused = () => {
    focusedItem.current = 0;
  };

  return {
    menuRef,
    items,
    focusedItem,
    focusNext,
    focusPrevious,
    focusCurrentItem,
    keyDown,
    controlMenu,
    firstItemFocused,
  };
};
